import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../components/Spinner";
import { PRODUCT_CONDITIONS } from "../constants/productCondition";

const API_BASE = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const DRAFT_KEY = "add_product_draft";

/* =========================
   Small Reusable UI Helpers
========================= */
const Required = () => <span className="text-danger">*</span>;

const emptyForm = {
  categoryId: "",
  subCategoryId: "",

  productName: "",
  description: "",
  sku: "",
  brand: "",
  modelNumber: "",

  price: "",
  discountPrice: "",
  costPrice: "",
  currency: "ETB",
  condition: "NEW", // default

  availableQuantity: "",

  weight: "",
  width: "",
  height: "",
  length: "",
  dimensionUnit: "cm",
  weightUnit: "kg",

  shippingClass: "",
  returnPolicy: "",
  warrantyPeriod: "",
};

export default function AddProduct() {
  const { auth } = useAuth();
  const sellerId = auth?.userId;

  const [step, setStep] = useState(1);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState({});

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageError, setImageError] = useState("");

  const [skuStatus, setSkuStatus] = useState("idle"); // idle | checking | valid | invalid
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  /* =========================
     Draft Restore
  ========================== */
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      const parsed = JSON.parse(draft);
      setForm(parsed.form || emptyForm);
      setStep(parsed.step || 1);
    }
  }, []);

  /* =========================
     Draft Auto Save
  ========================== */
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step }));
  }, [form, step]);

  /* =========================
     Fetch Categories
  ========================== */
  useEffect(() => {
    if (!API_BASE) return;
    fetch(`${API_BASE}/products/categories?page=0&size=50`)
      .then((res) => res.json())
      .then((res) => setCategories(res.content || []))
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load categories." }),
      );
  }, []);

  /* =========================
     Fetch Subcategories
  ========================== */
  useEffect(() => {
    if (!form.categoryId) {
      setSubCategories([]);
      return;
    }

    fetch(`${API_BASE}/products/subcategories/${form.categoryId}`)
      .then((res) => res.json())
      // .then((res) => setSubCategories(res.content || res || []))
      .then((res) => {
        if (Array.isArray(res)) {
          setSubCategories(res);
        } else if (Array.isArray(res.content)) {
          setSubCategories(res.content);
        } else {
          setSubCategories([]);
        }
      })
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load subcategories." }),
      );
  }, [form.categoryId]);

  /* =========================
     Image Preview + validation
  ========================== */

  useEffect(() => {
    if (!images.length) {
      setImagePreviews([]);
      return;
    }

    const urls = images.map((file) => URL.createObjectURL(file));
    setImagePreviews(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [images]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const maxSize = 1 * 1024 * 1024;

    const validFiles = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setImageError("Only JPEG, JPG, PNG, or WEBP images are allowed.");
        return;
      }
      if (file.size > maxSize) {
        setImageError("Each image must be under 1MB.");
        return;
      }
      validFiles.push(file);
    }

    setImageError("");

    // setImages((prev) => [...prev, ...files]);
    setImages((prev) => [...prev, ...validFiles]);
    // e.target.value = null;
    // setImages(files);
  };

  /* =========================
     SKU Uniqueness (Debounced)
  ========================== */
  useEffect(() => {
    if (!form.sku || !sellerId) return;
    if (!form.sku || form.sku.trim() === "") {
      setSkuStatus("idle");
      return;
    }
    const controller = new AbortController(); // prevents race condition

    const timer = setTimeout(async () => {
      try {
        setSkuStatus("checking");
        const res = await fetch(
          `${API_BASE}/products/sku-exists?sku=${form.sku}&sellerId=${sellerId}`,
          { signal: controller.signal },
        );

        const data = await res.json();
        if (data.exists) {
          setSkuStatus("invalid");
        } else {
          setSkuStatus("valid");
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("SKU check error", error);
          setSkuStatus("idle");
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort(); // cancel previous request
    };
  }, [form.sku, sellerId]);

  /* =========================
     Handlers
  ========================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    const numericFields = [
      "price",
      "discountPrice",
      "costPrice",
      "availableQuantity",
      "weight",
      "width",
      "height",
      "length",
    ];

    let finalValue = value;

    if (numericFields.includes(name)) {
      // Allow empty field while typing
      if (value === "") {
        finalValue = "";
      } else {
        const num = Number(value);

        // Prevent NaN and negative values
        if (isNaN(num) || num < 0) return;

        finalValue = num;
      }
    }

    setForm((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  /* =========================
     Validation
  ========================== */
  const requiredFields = {
    1: ["categoryId"],
    2: ["subCategoryId"],
    3: ["productName", "description", "sku", "price"],
    4: ["availableQuantity"],
  };

  const isStepValid = (s) => {
    const fields = requiredFields[s] || [];

    // 1 Required fields check
    const requiredValid = fields.every((f) => {
      const value = form[f];
      return (
        value !== undefined && value !== null && String(value).trim() !== ""
      );
    });

    if (!requiredValid) return false;

    // 2 SKU async validation (Step 3 only)
    if (s === 3 && skuStatus === "invalid") return false;

    // 3 Numeric validation (only if step 3)
    if (s === 3) {
      const numericFields = [
        "price",
        "availableQuantity",
        "discountPrice",
        "costPrice",
        "weight",
        "width",
        "height",
        "length",
      ];

      const numericValid = numericFields.every((f) => {
        const value = form[f];
        return value === "" || (typeof value === "number" && value >= 0);
      });

      if (!numericValid) return false;
    }

    return true;
  };

  const markStepTouched = (s) => {
    setTouched((prev) => {
      const next = { ...prev };
      (requiredFields[s] || []).forEach((f) => {
        next[f] = true;
      });
      return next;
    });
  };

  /* =========================
     Reset After Success
  ========================== */
  const resetAll = () => {
    localStorage.removeItem(DRAFT_KEY);
    setStep(1);
    setForm(emptyForm);
    setTouched({});
    setImages([]);
    setImagePreviews([]);
    setSkuStatus("idle");
  };

  /* =========================
     Submit
  ========================== */
  const handleSubmit = async () => {
    if (!isStepValid(4)) {
      markStepTouched(4);
      setStatus({
        type: "error",
        message: "Please complete all required fields.",
      });
      return;
    }

    setSubmitting(true);
    setStatus({ type: null, message: "" });

    try {
      const payload = {
        requestForProduct: {
          sellerId,
          productName: form.productName.trim(),
          description: form.description.trim(),
          sku: form.sku.trim(),
          brand: form.brand?.trim() || null,
          modelNumber: form.modelNumber?.trim() || null,

          price: Number(form.price),
          discountPrice: Number(form.discountPrice || 0),
          costPrice: Number(form.costPrice || 0),
          currency: form.currency,
          condition: form.condition,

          categoryId: Number(form.categoryId),
          subCategoryId: Number(form.subCategoryId),

          tags: [],
          attributes: {},

          weight: Number(form.weight || 0),
          width: Number(form.width || 0),
          height: Number(form.height || 0),
          length: Number(form.length || 0),
          dimensionUnit: form.dimensionUnit,
          weightUnit: form.weightUnit,

          shippingClass: form.shippingClass,
          returnPolicy: form.returnPolicy,
          warrantyPeriod: form.warrantyPeriod,
        },
        requestForInventory: {
          sellerId,
          availableQuantity: Number(form.availableQuantity),
        },
      };

      const formData = new FormData();
      formData.append(
        "request",
        new Blob([JSON.stringify(payload)], { type: "application/json" }),
      );

      images.forEach((f) => formData.append("imageFiles", f));

      const res = await fetch(`${API_BASE}/products/create`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Product creation failed");

      setStatus({
        type: "success",
        message: "Product created successfully.",
      });

      setTimeout(() => {
        resetAll();
        setStatus({ type: null, message: "" });
      }, 1500);
    } catch {
      setStatus({
        type: "error",
        message: "Unable to create product. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const StepIndicator = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between">
        {["Category", "Subcategory", "Details", "Inventory"].map(
          (label, index) => {
            const s = index + 1;
            return (
              <div
                key={s}
                className={`text-center flex-fill ${
                  step === s
                    ? "fw-bold text-primary"
                    : step > s
                      ? "text-success"
                      : "text-muted"
                }`}
              >
                <div
                  className={`rounded-circle mx-auto mb-1 ${
                    step >= s ? "bg-primary text-white" : "bg-light"
                  }`}
                  style={{
                    width: 32,
                    height: 32,
                    lineHeight: "32px",
                  }}
                >
                  {s}
                </div>
                <small>{label}</small>
              </div>
            );
          },
        )}
      </div>
      <hr />
    </div>
  );

  const step3Fields = [
    {
      name: "productName",
      label: "Product Name",
      type: "text",
      required: true,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: true,
    },
    { name: "sku", label: "SKU", type: "text", required: true },
    {
      name: "price",
      label: "Price",
      type: "number",
      required: true,
      min: 0,
      step: "0.01",
    },
    {
      name: "brand",
      label: "Brand",
      type: "text",
      required: false,
    },
    {
      name: "modelNumber",
      label: "Model Number",
      type: "text",
      required: false,
    },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      required: true,
      options: PRODUCT_CONDITIONS,
    },
  ];
  /* =========================
     UI
  ========================== */
  return (
    <div className="container mt-4 mb-5">
      <div className="card shadow border-0">
        <div className="card-body p-4">
          <h1>Add Product Form</h1>
          <StepIndicator />

          {status.type && (
            <div
              className={`alert ${
                status.type === "success" ? "alert-success" : "alert-danger"
              }`}
            >
              {status.message}
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <label className="form-label">
                Category <Required />
              </label>
              <select
                className={`form-select ${
                  touched.categoryId && !form.categoryId ? "is-invalid" : ""
                }`}
                name="categoryId"
                value={form.categoryId}
                onChange={handleChange}
              >
                <option value="">-- Select Category --</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <button
                className="btn btn-primary mt-3"
                onClick={() => {
                  if (!isStepValid(1)) return markStepTouched(1);
                  setStep(2);
                }}
              >
                Next
              </button>
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <label className="form-label">
                Subcategory <Required />
              </label>
              <select
                className={`form-select ${
                  touched.subCategoryId && !form.subCategoryId
                    ? "is-invalid"
                    : ""
                }`}
                name="subCategoryId"
                value={form.subCategoryId}
                onChange={handleChange}
              >
                <option value="">-- Select Subcategory --</option>
                {subCategories.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>

              <button
                className="btn btn-secondary mt-3 me-2"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button
                className="btn btn-primary mt-3"
                onClick={() => {
                  if (!isStepValid(2)) return markStepTouched(2);
                  setStep(3);
                }}
              >
                Next
              </button>
            </>
          )}

          {/* STEP 3 */}

          {step === 3 && (
            <>
              {step3Fields.map((field) => {
                const isInvalid = touched[field.name] && !form[field.name];

                // Special handling for SKU
                if (field.name === "sku") {
                  return (
                    <div key={field.name} className="mb-3">
                      <label className="form-label">
                        {field.label} {field.required && <Required />}
                      </label>

                      <input
                        type="text"
                        name="sku"
                        value={form.sku}
                        onChange={handleChange}
                        className={`form-control ${
                          skuStatus === "invalid"
                            ? "is-invalid"
                            : skuStatus === "valid"
                              ? "is-valid"
                              : isInvalid
                                ? "is-invalid"
                                : ""
                        }`}
                      />

                      {/* Checking */}
                      {skuStatus === "checking" && (
                        <small className="text-muted">
                          Checking SKU availability...
                        </small>
                      )}

                      {/* Already exists */}
                      {skuStatus === "invalid" && (
                        <div className="invalid-feedback">
                          SKU already exists
                        </div>
                      )}

                      {/* Available */}
                      {skuStatus === "valid" && (
                        <div className="valid-feedback">SKU available</div>
                      )}
                    </div>
                  );
                }

                //  Default fields
                return (
                  <div key={field.name} className="mb-3">
                    <label className="form-label">
                      {field.label} {field.required && <Required />}
                    </label>

                    {field.type === "textarea" ? (
                      <textarea
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        className="form-control"
                        required={field.required}
                      />
                    ) : field.type === "select" ? (
                      <select
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        className="form-select"
                        required={field.required}
                      >
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        className="form-control"
                        required={field.required}
                      />
                    )}
                  </div>
                );
              })}

              <button
                className="btn btn-secondary mt-3"
                onClick={() => setStep(2)}
              >
                Back
              </button>

              <button
                className="btn btn-primary mt-3"
                onClick={() => {
                  if (!isStepValid(3)) return markStepTouched(3);
                  setStep(4);
                }}
              >
                Next
              </button>
            </>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <>
              <label className="form-label">
                Available Quantity <Required />
              </label>
              <input
                className={`form-control mb-2 ${
                  touched.availableQuantity && !form.availableQuantity
                    ? "is-invalid"
                    : ""
                }`}
                name="availableQuantity"
                value={form.availableQuantity}
                onChange={handleChange}
              />
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                className="form-control mb-2"
                onChange={handleImageChange}
              />

              <div className="d-flex gap-2 flex-wrap mb-3">
                {imagePreviews.map((src, i) => (
                  <div key={i} className="position-relative">
                    <img
                      src={src}
                      alt="preview"
                      className="rounded border"
                      style={{ width: 100, height: 100, objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0"
                      onClick={() => {
                        setImages((prev) =>
                          prev.filter((_, index) => index !== i),
                        );
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-secondary me-2"
                onClick={() => setStep(3)}
              >
                Back
              </button>

              <button
                className="btn btn-success"
                disabled={submitting || !isStepValid(4)}
                onClick={handleSubmit}
              >
                {submitting ? (
                  <span className="d-flex align-items-center gap-2">
                    <Spinner text="Creating" /> Creating...
                  </span>
                ) : (
                  "Create Product"
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
